import React, { useState, useEffect } from 'react';
import { webrtcVoiceService } from '../services/webrtcService';
import { Medium, MediumDomain } from '../types';
import { 
  PhoneIcon, 
  HeartIcon, 
  BriefcaseIcon, 
  TrendingUpIcon, 
  CrownIcon, 
  StarIcon, 
  SparklesIcon, 
  XIcon 
} from './Icons';
import { isMediumFavorite, toggleFavoriteMedium } from './favorites';
import { useClient } from '../contexts/ClientContext';
import { getMediumDomain, isMediumVip } from '../data/mediums';

interface MediumCardProps {
  medium: Medium;
  onChatStart: (medium: Medium) => void;
  onProfileClick: (medium: Medium) => void;
}

export const MediumCard: React.FC<MediumCardProps> = ({ medium, onChatStart, onProfileClick }) => {
  const { client, openAuthModal } = useClient();
  const [isFav, setIsFav] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);

  useEffect(() => {
      setIsFav(isMediumFavorite(medium.id));
      const handleFavUpdate = () => {
          setIsFav(isMediumFavorite(medium.id));
      };
      window.addEventListener('astro_favorites_updated', handleFavUpdate);
      return () => window.removeEventListener('astro_favorites_updated', handleFavUpdate);
  }, [medium.id]);

  const domain: MediumDomain = medium.domain || getMediumDomain(medium);
  const isVip: boolean = medium.isVip !== undefined ? Boolean(medium.isVip) : isMediumVip(medium);
  const clientPoints: number = client?.loyaltyPoints || 0;
  const isVipLocked: boolean = isVip && (!client || clientPoints < 1000);
  const isOnline = medium.status === 'available';

  // Thèmes de couleurs très attirants par domaine sans aucun émoji
  const domainConfig = {
    Amour: {
      label: 'Amour',
      icon: <HeartIcon className="w-3 h-3 text-pink-300 fill-pink-400/40" />,
      badgeClass: 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 text-pink-300 border-pink-500/40 shadow-[0_0_12px_rgba(244,63,94,0.3)]',
      cardHoverClass: 'hover:border-pink-500/70 hover:shadow-[0_0_25px_rgba(244,63,94,0.35)]',
      avatarRing: 'border-pink-500/40 group-hover:border-pink-400 shadow-[0_0_10px_rgba(244,63,94,0.25)]',
      btnChatClass: 'bg-gradient-to-r from-pink-600 via-rose-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white border-pink-400/40'
    },
    Travail: {
      label: 'Travail',
      icon: <BriefcaseIcon className="w-3 h-3 text-cyan-300" />,
      badgeClass: 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border-cyan-500/40 shadow-[0_0_12px_rgba(6,182,212,0.3)]',
      cardHoverClass: 'hover:border-cyan-400/70 hover:shadow-[0_0_25px_rgba(6,182,212,0.35)]',
      avatarRing: 'border-cyan-500/40 group-hover:border-cyan-400 shadow-[0_0_10px_rgba(6,182,212,0.25)]',
      btnChatClass: 'bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 hover:from-cyan-500 hover:to-blue-500 text-white border-cyan-400/40'
    },
    Finance: {
      label: 'Finances',
      icon: <TrendingUpIcon className="w-3 h-3 text-amber-300" />,
      badgeClass: 'bg-gradient-to-r from-amber-500/20 to-emerald-500/20 text-amber-300 border-amber-500/40 shadow-[0_0_12px_rgba(245,158,11,0.3)]',
      cardHoverClass: 'hover:border-amber-400/70 hover:shadow-[0_0_25px_rgba(245,158,11,0.35)]',
      avatarRing: 'border-amber-500/40 group-hover:border-amber-400 shadow-[0_0_10px_rgba(245,158,11,0.25)]',
      btnChatClass: 'bg-gradient-to-r from-amber-600 via-yellow-600 to-emerald-600 hover:from-amber-500 hover:to-emerald-500 text-white border-amber-400/40'
    }
  }[domain] || {
    label: 'Général',
    icon: <SparklesIcon className="w-3 h-3 text-purple-300" />,
    badgeClass: 'bg-purple-900/40 text-purple-300 border-purple-500/40',
    cardHoverClass: 'hover:border-purple-500/70',
    avatarRing: 'border-purple-500/40',
    btnChatClass: 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
  };

  const handleStartChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isVipLocked) {
      setShowVipModal(true);
      return;
    }
    onChatStart(medium);
  };

  const handleVoiceCall = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isVipLocked) {
        setShowVipModal(true);
        return;
      }

      const currentClient = client || {
          id: 'client_' + Math.random().toString(36).substring(2, 7),
          name: 'Client Découverte',
          address: 'France',
          loyaltyPoints: 100
      };
      
      webrtcVoiceService.startCall(
          medium.id.toString(),
          medium.name,
          medium.imageUrl,
          currentClient.id,
          currentClient.name,
          currentClient.address || 'France',
          currentClient.loyaltyPoints || 100
      );
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!client) {
          openAuthModal();
          return;
      }
      toggleFavoriteMedium(medium.id);
  };

  const handleUnlockVipPack = () => {
    setShowVipModal(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('astro_open_purchase_chat', { detail: { pack: '100' } }));
    }
  };

  return (
    <>
      <div 
        onClick={() => onProfileClick(medium)}
        className={`group relative bg-[#12102A]/90 hover:bg-[#1A1033] border rounded-3xl p-4 transition-all duration-300 shadow-xl flex flex-col justify-between cursor-pointer hover:-translate-y-1 select-none ${
          isVip 
            ? 'border-amber-400/50 hover:border-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.15)] hover:shadow-[0_0_30px_rgba(251,191,36,0.35)]' 
            : `border-purple-900/40 ${domainConfig.cardHoverClass}`
        }`}
      >
        {/* Top Bar: Status, Domain Badge, VIP Badge & Favorite */}
        <div className="flex justify-between items-center gap-1.5 mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Statut En ligne / Occupé */}
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
              isOnline
                ? 'bg-emerald-950/80 text-emerald-300 border-emerald-500/40'
                : 'bg-zinc-900/80 text-zinc-400 border-zinc-700/40'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-500'}`}></span>
              <span>{isOnline ? 'En ligne' : 'Occupé'}</span>
            </span>

            {/* Badge Domaine avec Couleur Attirante (SVG icon) */}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold border ${domainConfig.badgeClass}`}>
              {domainConfig.icon}
              <span>{domainConfig.label}</span>
            </span>

            {/* Badge VIP (+1000 Astres) */}
            {isVip && (
              <span 
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black tracking-wider bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 text-black shadow-[0_0_12px_rgba(251,191,36,0.6)] border border-amber-100 uppercase"
                title="Réservé aux membres avec +1000 Astres"
              >
                <CrownIcon className="w-3 h-3 fill-black text-black" />
                <span>VIP 1000+</span>
              </span>
            )}
          </div>

          <button
            onClick={handleToggleFavorite}
            className="p-1.5 rounded-full bg-black/40 hover:bg-black/60 border border-purple-900/40 text-purple-300 hover:text-pink-400 transition-colors shrink-0"
            title={isFav ? "Retirer des favoris" : "Ajouter aux favoris"}
          >
            <HeartIcon className={`w-3.5 h-3.5 ${isFav ? 'fill-pink-500 text-pink-500' : ''}`} />
          </button>
        </div>

        {/* Main Info Area */}
        <div className="flex items-center gap-3.5 mb-4">
          {/* Avatar with Ring */}
          <div className="relative shrink-0">
            <img 
              src={medium.imageUrl} 
              alt={medium.name}
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 transition-all ${
                isVip 
                  ? 'border-amber-400 ring-2 ring-amber-400/40 shadow-[0_0_15px_rgba(251,191,36,0.4)]' 
                  : domainConfig.avatarRing
              }`}
              onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }}
            />
            {isVip && (
              <span className="absolute -bottom-1 -right-1 bg-amber-400 text-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-black shadow-md border border-white">
                <CrownIcon className="w-3 h-3 fill-black text-black" />
              </span>
            )}
          </div>

          {/* Name & Specialty & Rating */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="text-sm sm:text-base font-bold text-white group-hover:text-amber-200 transition-colors truncate">
                {medium.name}
              </h3>
            </div>

            <p className="text-[11px] text-purple-200/80 truncate mt-0.5 font-medium">
              {medium.specialties.slice(0, 2).join(' • ')}
            </p>
            
            <div className="flex items-center gap-1.5 text-xs text-amber-300 font-bold mt-1">
              <StarIcon className="w-3 h-3 fill-amber-300 text-amber-300 inline" />
              <span>{medium.rating}</span>
              <span className="text-[10px] text-purple-300/60 font-light font-sans">
                ({medium.reviewsList?.length || medium.reviews || 215} avis)
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons: Consulter (Chat) & Appel */}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-purple-900/30">
          <button
            onClick={handleStartChat}
            className={`w-full py-2 rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer text-center flex items-center justify-center gap-1.5 border ${
              isVip && isVipLocked
                ? 'bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-yellow-300 text-black border-amber-200 shadow-[0_0_12px_rgba(251,191,36,0.4)]'
                : isVip
                  ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black border-amber-300'
                  : domainConfig.btnChatClass
            }`}
          >
            {isVipLocked ? (
              <>
                <CrownIcon className="w-3.5 h-3.5 fill-black text-black" />
                <span>Consulter VIP</span>
              </>
            ) : (
              <span>Consulter</span>
            )}
          </button>

          <button
            onClick={handleVoiceCall}
            className={`w-full py-2 rounded-xl font-bold text-xs shadow-md transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1 border ${
              !isOnline
                ? 'bg-zinc-900 text-zinc-500 border-zinc-800 cursor-not-allowed'
                : isVip && isVipLocked
                  ? 'bg-amber-950/70 hover:bg-amber-900 text-amber-300 border-amber-500/50'
                  : 'bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 border-emerald-500/40'
            }`}
            disabled={!isOnline}
          >
            <PhoneIcon className={`w-3 h-3 ${isOnline ? (isVip && isVipLocked ? 'text-amber-400' : 'text-emerald-400') : 'text-zinc-600'}`} />
            <span>{isVip && isVipLocked ? 'Appel VIP' : 'Appel'}</span>
          </button>
        </div>

      </div>

      {/* MODAL SALON VIP (+1000 ASTRES REQUIS) */}
      {showVipModal && (
        <div 
          className="fixed inset-0 z-[999999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowVipModal(false)}
        >
          <div 
            className="bg-gradient-to-b from-[#1E1236] via-[#120B24] to-[#0A0614] border-2 border-amber-400/80 rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-[0_0_60px_rgba(251,191,36,0.35)] text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setShowVipModal(false)}
              className="absolute top-4 right-4 text-purple-300 hover:text-white bg-white/5 hover:bg-white/10 w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors"
            >
              <XIcon className="w-4 h-4" />
            </button>

            {/* Crown Header */}
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center mx-auto mb-3 shadow-[0_0_30px_rgba(251,191,36,0.5)] animate-pulse">
              <CrownIcon className="w-8 h-8 text-black fill-black" />
            </div>

            <span className="text-[11px] font-black uppercase tracking-widest text-amber-300 block mb-1">
              Salon Privilège AstroX
            </span>
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-white tracking-tight">
              Médium d'Élite Réservé VIP
            </h2>

            {/* Profile Snapshot */}
            <div className="my-4 bg-black/40 border border-amber-400/40 rounded-2xl p-3 flex items-center gap-3 text-left">
              <img 
                src={medium.imageUrl} 
                alt={medium.name}
                className="w-14 h-14 rounded-full object-cover border-2 border-amber-400 shadow-md"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="text-base font-bold text-white">{medium.name}</h4>
                  <span className="text-[10px] bg-amber-400 text-black px-1.5 py-0.5 rounded font-black flex items-center gap-1">
                    <CrownIcon className="w-2.5 h-2.5 fill-black text-black" />
                    VIP
                  </span>
                </div>
                <p className="text-xs text-purple-200/80 truncate">
                  {medium.specialties.slice(0, 2).join(' • ')}
                </p>
                <div className="flex items-center gap-1.5 text-xs text-amber-300 font-bold mt-0.5">
                  <StarIcon className="w-3 h-3 fill-amber-300 text-amber-300 inline" />
                  <span>{medium.rating}</span>
                  <span className="text-[10px] text-purple-300/60 font-light">
                    ({medium.reviewsList?.length || medium.reviews || 215} consultations d'exception)
                  </span>
                </div>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-purple-200/90 leading-relaxed mb-4">
              Ce praticien de haute renommée est réservé en exclusivité aux consultants membres du <strong className="text-amber-300">Cercle Privilège</strong> disposant d'au moins <strong className="text-amber-300">1 000 Astres</strong>.
            </p>

            {/* Solde actuel de l'utilisateur */}
            <div className="bg-purple-950/40 border border-purple-800/40 rounded-2xl p-3 mb-5 text-left">
              <div className="flex justify-between items-center text-xs mb-1">
                <span className="text-purple-300 font-medium">Votre Solde Actuel :</span>
                <span className="text-amber-300 font-black text-sm">{clientPoints} Astres</span>
              </div>
              <div className="w-full bg-black/60 rounded-full h-2 overflow-hidden border border-purple-900/40 mb-2">
                <div 
                  className="bg-gradient-to-r from-amber-400 to-yellow-300 h-full rounded-full transition-all"
                  style={{ width: `${Math.min(100, (clientPoints / 1000) * 100)}%` }}
                ></div>
              </div>
              <p className="text-[11px] text-purple-300/80">
                {client ? (
                  <>Il vous manque <span className="text-amber-300 font-bold">{Math.max(0, 1000 - clientPoints)} Astres</span> pour débloquer l'accès prioritaire à ce voyant.</>
                ) : (
                  <>Connectez-vous ou rechargez votre compte pour débloquer l'accès prioritaire à ce voyant.</>
                )}
              </p>
            </div>

            {/* CTAs */}
            <div className="space-y-2.5">
              <button
                onClick={handleUnlockVipPack}
                className="w-full py-3 px-4 rounded-2xl bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 hover:from-amber-300 hover:to-yellow-200 text-black font-black text-xs sm:text-sm shadow-[0_0_25px_rgba(251,191,36,0.5)] transition-all transform hover:scale-[1.02] active:scale-95 cursor-pointer flex items-center justify-center gap-2 border border-amber-100 uppercase"
              >
                <CrownIcon className="w-4 h-4 fill-black text-black" />
                <span>Débloquer avec le Pack Privilège (100 € • 1 150 Astres)</span>
              </button>

              <button
                onClick={() => setShowVipModal(false)}
                className="w-full py-2.5 px-4 rounded-xl bg-purple-900/30 hover:bg-purple-900/50 text-purple-300 hover:text-white text-xs font-semibold transition-colors cursor-pointer"
              >
                Consulter un médium disponible sans condition
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default MediumCard;
